/**
 * Creates an errorData object used in the Error component.
 * @param {Object} params - The error data parameters.
 * @param {string} params.description - The error description.
 * @param {string} params.header - The error header.
 * @param {string} params.status - The error status.
 * @returns {{ERROR_DESCRIPTION: string, ERROR_HEADER: string, STATUS_ERROR: string}} The error data object.
 */
export const createErrorData = ({ description = '', header = '', status = '' }) => ({
  ERROR_DESCRIPTION: description,
  ERROR_HEADER: header,
  STATUS_ERROR: status,
});

/**
 * Default error data used as a placeholder for when no specific error information
 * is available.
 */
export const defaultErrorData = createErrorData({
  description: "There's insufficient sample to run this Narrative based on the answers provided.",
  header: 'Insufficient Sample',
  status: 'failed-flow',
});

/**
 * used to increase readability for Narrative status
 */
export const narrativeStatus = Object.freeze({
  CANCELED: 'CANCELED',
  CLEANUP: 'CLEANUP',
  COMPLETED: 'COMPLETED',
  EXCEPTION: 'EXCEPTION',
  INVALIDATED: 'INVALIDATED',
  NOT_STARTED: 'NOT_STARTED',
  QUEUED: 'QUEUED',
  RUNNING: 'RUNNING',
  SETUP: 'SETUP',
});

/**
 * An enumeration of possible tags assigned to Flow Topics.
 * @returns {Object}
 */
export const flowTopicTag = Object.freeze({
  INCLUDES_SUBSCRIBED_CATEGORIES: 'INCLUDES_SUBSCRIBED_CATEGORIES',
  NO_SAMPLE: 'NO_SAMPLE',
  PREVIEW: 'PREVIEW',
});

/**
 * An enumeration of possible tags assigned to Flows.
 * @returns {Object}
 */
export const flowTag = Object.freeze({
  NO_SAMPLE: 'NO_SAMPLE',
});

/**
 * Narrative error states
 * @returns {Status[]}
 */
export const narrativeErrorStatuses = [
  narrativeStatus.EXCEPTION,
  narrativeStatus.CANCELED,
  narrativeStatus.INVALIDATED,
];

/**
 * Narrative states where we show a loading spinner
 * @returns {Status[]}
 */
export const narrativeFlowPendingStatuses = [
  narrativeStatus.QUEUED,
  narrativeStatus.NOT_STARTED,
  narrativeStatus.SETUP,
];

export const EMPTY_TOPIC_IDS = [];

/**
 * Normalizes a topic id list by removing null, undefined, and blank string values.
 * @param {Array<string | number | null | undefined>} [topicIds=[]]
 * @returns {Array<string | number>}
 */
export const normalizeTopicIds = (topicIds = EMPTY_TOPIC_IDS) =>
  Array.isArray(topicIds)
    ? topicIds.filter(
        (topicId) => topicId != null && (typeof topicId !== 'string' || topicId.trim().length > 0)
      )
    : [];
